import { Component } from 'react';
import { withTranslation } from 'react-i18next';

import DataTable from '../../../../components/Datatable';
import Loading from '../../../../components/Loading';
import { getSortKeys, setAdditionalStyles } from '../../../../helpers/table';
import { reportsPosition } from '../../GscTools/metrics';
import { ROW_CAP } from '../../GscTools/query';
import { formatPeriod } from '../../GscTools/period';
import { pageActionColumn, pageColumns } from '../keys';

/**
 * Block 04 — the exact pages Search Console returned under the active group's rules.
 *
 * Evidence, not an inventory. Search Console withholds rows below its own thresholds and the
 * retrieval is bounded, so a short list means a short list was returned; it never means the group
 * is small, and an empty one never proves the group has no URLs. Load more asks for the next
 * `startRow` under the same rule until the product cap, and the note under the table says which of
 * the three ways retrieval stopped.
 *
 * Each URL names the other groups it also meets, checked in the browser against their rules.
 * That is how an overlap is shown without being acted on: nothing is moved, subtracted or
 * deduplicated, and every group's figures in block 02 stay its own response.
 *
 * The URLs are kept verbatim — never lower-cased, never stripped of a query string or a trailing
 * slash — because that is the exact string the next tool has to be handed: the row's links open
 * its queries in Pages, Queries & Changes, or fill URL Inspection without inspecting it.
 */
class MatchingPages extends Component {
    constructor(props) {
        super(props);
        this.searchFunction = this.searchFunction.bind(this);
    }

    searchFunction(data, search) {
        if (!search) return data;
        const term = search.toLowerCase();
        return data.filter((item) => item.key?.toLowerCase().includes(term));
    }

    render() {
        const {
            t,
            scope,
            group,
            period,
            shortened,
            status,
            error,
            rows,
            stopReason,
            loadingMore,
            onLoadMore,
            onExport,
        } = this.props;

        const columns = pageColumns(t, { hasPosition: reportsPosition(scope.searchType) });
        const tableColumns = [...columns, pageActionColumn(t, scope, period)];
        const overlapping = rows.filter((row) => row.alsoIn.length).length;

        return (
            <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
                <div className="card-header d-flex justify-content-between">
                    <div>
                        <p className="card-title">04 · {t('tools.content_groups.pages.title')}</p>
                        <p className="gsc-tool-sub">
                            {group
                                ? `${group} · ${formatPeriod(period)}`
                                : t('tools.content_groups.pages.description')}
                        </p>
                    </div>

                    {!!group && (
                        <div className="gsc-tool-icon-actions">
                            <button
                                type="button"
                                title={t('tools.content_groups.pages.export')}
                                aria-label={t('tools.content_groups.pages.export')}
                                onClick={onExport}
                                disabled={status !== 'done' || !rows.length}
                            >
                                <i className="icon-fi_download" />
                            </button>
                        </div>
                    )}
                </div>

                <div className="card-body">
                    {!group && (
                        <div className="gsc-tool-load-state">
                            <span className="gsc-tool-load-icon" aria-hidden="true">
                                <i className="icon-pages" />
                            </span>
                            <h4>{t('tools.content_groups.pages.idle_title')}</h4>
                            <p>{t('tools.content_groups.pages.idle')}</p>
                        </div>
                    )}

                    {/* Said before the rows, not under them: a reader comparing this block with
                        the totals above has to know the windows differ before they conclude the
                        numbers disagree. */}
                    {!!group && shortened && (
                        <div className="gsc-tool-banner">
                            {t('tools.content_groups.pages.shortened', {
                                period: formatPeriod(period),
                            })}
                        </div>
                    )}

                    {!!group && status === 'loading' && <Loading padding={20} />}

                    {!!group && status === 'error' && (
                        <div className="gsc-tool-banner error">
                            {error ?? t('global.something_went_wrong')}
                        </div>
                    )}

                    {!!group && status === 'done' && (
                        <>
                            {/* A failure while loading more keeps the rows already retrieved. */}
                            {!!error && <div className="gsc-tool-banner error">{error}</div>}

                            {!!overlapping && (
                                <div className="gsc-tool-banner">
                                    {t('tools.content_groups.pages.overlap', {
                                        overlapping,
                                        rows: rows.length,
                                    })}
                                </div>
                            )}

                            <DataTable
                                nodes={rows}
                                COLUMNS={tableColumns}
                                customColumnsForExport={columns}
                                sortFns={getSortKeys(tableColumns)}
                                additionalStyles={setAdditionalStyles(tableColumns)}
                                searchFunction={this.searchFunction}
                                fileName={t('tools.content_groups.pages.file', { group })}
                                customEmptyMessage={t('tools.content_groups.pages.empty')}
                                customContainerStyles="gsc-tool-key-table"
                                loadMore={onLoadMore}
                                loadMoreLoading={loadingMore}
                                showLoadMoreBtn={stopReason === 'more_available'}
                            />

                            <p className="gsc-tool-table-note">
                                {t(`tools.gsc.stop.${stopReason}`)}{' '}
                                {t('tools.content_groups.pages.retrieved', {
                                    rows: rows.length,
                                    cap: ROW_CAP,
                                })}
                            </p>
                            <p className="gsc-tool-table-note">
                                {t('tools.content_groups.pages.not_inventory')}
                            </p>
                            <p className="gsc-tool-table-note">
                                {t('tools.content_groups.pages.links_note')}
                            </p>
                        </>
                    )}
                </div>
            </div>
        );
    }
}

export default withTranslation()(MatchingPages);
